import { Button, Typography } from "@mui/material";
import { classes } from "portal/theme/theme";
import { HealthLog } from "protos/portal/health";
import { Map } from "portal/components/map/Map";
import { RobotBlockHistory } from "portal/views/robots/useRobotHistory";
import {
  RobotSummaryCheckedin,
  RobotSummaryHeadlineInfo,
  RobotSummaryIcon,
  RobotSummaryText,
} from "portal/components/robots/RobotSummaryCard";
import { RobotSummaryResponse } from "protos/portal/robots";
import { RobotView } from "../../views/robots/AnyRobotPage";
import {
  SupportLinkProps,
  SupportLinksCard,
} from "portal/components/robots/SupportLinksCard";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { valueOrLoading } from "portal/utils/strings";
import { withAuthenticationRequired } from "@auth0/auth0-react";
import { WithSkeleton } from "portal/components/WithSkeleton";
import React, { FunctionComponent, ReactElement } from "react";

interface RobotSummaryMapInfo {
  summary: RobotSummaryResponse | undefined;
  isHistoryLoading: boolean;
  hasBlocks: boolean | undefined;
  healthLogs: HealthLog[] | undefined;
  blocks: RobotBlockHistory | undefined;
}

interface RobotSummaryProps {
  success: boolean;
  serial: string | undefined;
  customerSerial: string | undefined;
  headlineInfo: RobotSummaryHeadlineInfo;
  mapInfo: RobotSummaryMapInfo;
  summaryCard: ReactElement;
  offerViewSwitchButton?: RobotView;
  machineHealthCards: ReactElement[];
  supportLinks: SupportLinkProps[];
}

const _RobotSummary: FunctionComponent<RobotSummaryProps> = ({
  success,
  serial,
  customerSerial,
  headlineInfo,
  mapInfo,
  summaryCard,
  offerViewSwitchButton,
  machineHealthCards,
  supportLinks,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  let viewSwitchButtonString = "";

  switch (offerViewSwitchButton) {
    case RobotView.Atk: {
      viewSwitchButtonString = t("views.fleet.robots.switchView.atk");
      break;
    }
    case RobotView.LaserWeeder: {
      viewSwitchButtonString = t("views.fleet.robots.switchView.laserweeder");
      break;
    }
  }

  return (
    <>
      <WithSkeleton
        variant="rectangular"
        success={success}
        className="min-h-10"
      >
        <div className="flex items-center flex-wrap gap-4">
          <RobotSummaryIcon {...headlineInfo} className="text-4xl" />
          <Typography variant="h1" className="text-4xl">
            {valueOrLoading(customerSerial, t)}
          </Typography>
          <RobotSummaryText {...headlineInfo} className="text-sm font-normal" />
          <RobotSummaryCheckedin
            {...headlineInfo}
            className="text-sm font-normal"
          />
          <div className="flex-1" />
          {offerViewSwitchButton && (
            <Button onClick={() => navigate(`?view=${offerViewSwitchButton}`)}>
              {t(viewSwitchButtonString)}
            </Button>
          )}
        </div>
      </WithSkeleton>
      <div
        className={classes("flex flex-col md:flex-row", "w-full gap-8 mt-8")}
      >
        <Map
          className="w-full md:w-1/2 lg:w-2/3 min-h-96"
          customerUuid={mapInfo.summary?.customer?.uuid}
          robots={mapInfo.summary ? [mapInfo.summary] : []}
          loading={mapInfo.isHistoryLoading}
          history={mapInfo.hasBlocks ? undefined : mapInfo.healthLogs}
          blocks={mapInfo.blocks}
          key={serial}
        />
        <WithSkeleton
          variant="rectangular"
          success={success}
          className="w-full md:w-1/2 lg:w-1/3 flex-shrink-0 flex-grow"
        >
          <div className="w-full flex flex-col gap-8">
            {summaryCard}
            {supportLinks.length > 0 && (
              <SupportLinksCard links={supportLinks} success={success} />
            )}
          </div>
        </WithSkeleton>
      </div>
      {machineHealthCards.length > 0 && (
        <>
          <Typography variant="h4" className="text-center my-8 text-xl">
            {t("views.fleet.robots.summary.machineHealth")}
          </Typography>
          <div className="flex flex-col md:flex-row items-stretch gap-8">
            {machineHealthCards}
          </div>
        </>
      )}
    </>
  );
};

export const RobotSummary = withAuthenticationRequired(_RobotSummary);
